import { useState, useEffect, useRef, useCallback } from "react";
import H2 from "@/atoms/text/H2";
import H3 from "@/atoms/text/H3";
import H4 from "@/atoms/text/H4";
import Para from "@/components/atoms/text/Para";
import { ChevronArrowIcon } from "@/atoms/Icons";
import { isMobileScreen } from "@/utils/functions";
import PropTypes from "prop-types";

export default function Faq({ data }) {
  const [openIndices, setOpenIndices] = useState([]);
  const [isMobile, setIsMobile] = useState(null);
  const faqRefs = useRef([]);

  useEffect(() => {
    setIsMobile(isMobileScreen());
  }, []);

  const toggleFaq = useCallback((index) => {
    setOpenIndices((prevIndices) => {
      const newIndices = new Set(prevIndices);
      if (newIndices.has(index)) {
        newIndices.delete(index);
      } else {
        newIndices.add(index);
      }
      return Array.from(newIndices);
    });
  }, []);

  function FaqContact() {
    return (
      <div className="faq-contact w-100">
        <H4 className="mt-28 mb-3 bold">Have questions?</H4>
        <Para className="text--lg">
          Call us{" "}
          <a href="tel:+18004618535" className="tc-blue">
            (800) 461-8535
          </a>{" "}
          or <br />
          email us{" "}
          <a href="mailto:sales@answerforce.com" className="tc-blue">
            sales@answerforce.com
          </a>
        </Para>
      </div>
    );
  }

  return (
    <section className="faq bc-blue-light pv-15-d pv-8">
      <div className="container fx--c-m">
        {/* Left Section - Sticky FAQ Title */}
        <div className="faq-title">
          <H2 color="black" className="bold">
            Frequently asked questions<span className="dot">.</span>
          </H2>
          {!isMobile && (
            <div className="faq-contact-desktop">
              <FaqContact />
            </div>
          )}
        </div>

        {/* Right Section - Accordion List */}
        <ul className="accordion">
          {data.map(
            (
              { question, answer, answerInList, orderedList, additionalInfo },
              idx
            ) => {
              const isOpen = openIndices.includes(idx);

              return (
                <li
                  ref={(el) => (faqRefs.current[idx] = el)}
                  onClick={() => toggleFaq(idx)}
                  key={idx}
                  className={`accordion-list bc-white pv-6-d pl-6-d pr-6-d pv-4 pl-4 pr-4 ${
                    idx === data.length - 1 ? "" : "mb-7-d mb-5"
                  } ${isOpen ? "is-open" : ""}`}
                >
                  <div className="fx fx--cv-d fx--sb fx--ns">
                    <H3
                      desktopSize="xs"
                      mobileSize="xs"
                      className="bold mr-4 mr-reset-d"
                      dangerouslySetInnerHTML={{ __html: question }}
                    />
                    <ChevronArrowIcon
                      width="18"
                      height="18"
                      className={"accordion-arrow"}
                      color="#33393d"
                    />
                  </div>
                  <div
                    className={`faq-content ${isOpen ? "is-open" : ""}`}
                    onTransitionEnd={() => {
                      if (isOpen) {
                        faqRefs.current[idx].style.height = "auto";
                      }
                    }}
                  >
                    {answer && (
                      <Para
                        desktopSize="lg"
                        mobileSize="lg"
                        className="pr-5-d pr-6"
                        dangerouslySetInnerHTML={{
                          __html: Array?.isArray(answer)
                            ? answer
                                .map(
                                  (item) =>
                                    `<span class="mt-2 fx step-label">${item}</span>`
                                )
                                .join("")
                            : `<span class="mt-2 step-label">${answer}</span>`,
                        }}
                      />
                    )}
                    {answerInList &&
                      (orderedList ? (
                        <ol className="mt-5-d text tc-black text--lg ml-4">
                          {answerInList.map((item, idx) => (
                            <li
                              key={idx}
                              dangerouslySetInnerHTML={{ __html: item }}
                              className="mt-1 pl-1"
                            />
                          ))}
                        </ol>
                      ) : (
                        <ul className="bullet-list mt-5-d ml-5 text tc-black text--lg">
                          {answerInList.map((item, idx) => (
                            <li
                              key={idx}
                              dangerouslySetInnerHTML={{ __html: item }}
                              className="mt-1"
                            />
                          ))}
                        </ul>
                      ))}
                    {additionalInfo && (
                      <Para
                        desktopSize="lg"
                        mobileSize="lg"
                        className="pr-5-d pr-6"
                        dangerouslySetInnerHTML={{
                          __html: `<span class="mt-2 step-label">${additionalInfo}</span>`,
                        }}
                      />
                    )}

                    <style jsx global>{`
                      p > span b {
                        white-space: nowrap;
                      }
                      .step-label {
                        display: inline-block;
                        min-width: 100px;
                      }
                    `}</style>
                  </div>
                </li>
              );
            }
          )}
        </ul>
        {isMobile && <FaqContact />}
      </div>
    </section>
  );
}

Faq.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      question: PropTypes.string,
      answer: PropTypes.string,
      answerInList: PropTypes.arrayOf(PropTypes.string),
    })
  ),
};
